import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
// Rxjs
import { Observable , of} from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
// Environment
import { environment } from 'src/environments/environment';
// Model
import { MessageType } from '../../components/messages/message-handler/message-handler.component';
import { MessageHandlerService } from '../../components/messages/message-handler/message-handler.service';
import { SaveType } from '../../core/models/enumerations/save-type';
// Interface
import { TranslateService } from '@ngx-translate/core';
import { LoadWheelService } from '../../components/load-wheel/load-wheel.service';


import { PipelineLTSASearch, PipelineLTSASearchAdapter } from 'src/app/core/models/pipeline-ltsa-search';
import { PipelinesLTSADisplay } from 'src/app/core/models/pipeline-ltsa/display/pipelines-ltsa-display';
import { EntityName } from '../../core/models/enumerations/entity-name';
import { SpinnerProcess } from '../../core/models/spinner-process';
import { RecordLockingFlow } from '../../core/record-locking/record-locking-flow';
import { EntityCommonService } from '../../core/services/entity-common.service';
import { LockSameUserMessageService, LockSameUserResult } from '../../core/services/lock-same-user-message.service';
import { TelemetryService } from '../../core/services/telemetry.service';
import { EntityUtilsService } from '../../core/utils/entity-utils.service';

@Injectable({
    providedIn: 'root'
})
export class PipelineLtsaSectionService {

    constructor(
        private readonly adapter: PipelineLTSASearchAdapter,
        private readonly recordLockingFlow: RecordLockingFlow,
        private readonly http: HttpClient,
        private readonly messageHandlerService: MessageHandlerService,
        private readonly loadWheelService: LoadWheelService,
        private readonly entityCommonService: EntityCommonService,
        private readonly translate: TranslateService,
        private readonly pipelineLTSASearchAdapter: PipelineLTSASearchAdapter,
        private readonly entityUtilsService: EntityUtilsService,
        private readonly lockSameUserMessageService: LockSameUserMessageService,
        private readonly telemetry: TelemetryService,
    ) {
    }

    getPipelineLtsaDetailsById(ltsaId: string, isReload: boolean = false, quickAccess: boolean = false): Promise<PipelinesLTSADisplay> {
        let mainLockID: number = 0;

        if (isReload) {
            mainLockID = this.recordLockingFlow.getLockID(EntityName.PIPELINE_LTSA, ltsaId);
        }

        return new Promise<PipelinesLTSADisplay>((resolve) => {
            this.lockSameUserMessageService.validateEntityIsLockedByUser(this.recordLockingFlow, EntityName.PIPELINE_LTSA, ltsaId, mainLockID, quickAccess).then((lockSameUserResult: LockSameUserResult) => {
                if(lockSameUserResult.openEntity) {
                    this.getEntityDetailsById(ltsaId, mainLockID, false).subscribe((entityDisplay: PipelinesLTSADisplay) => {
                        resolve(entityDisplay);
                    });
                } else {
                    resolve(PipelinesLTSADisplay.BuildPipelineLTSADisplay({}));
                }
            });
        });
    }

    private getEntityDetailsById(ltsaId: string, mainLockID: number, onlyForRead: boolean): Observable<PipelinesLTSADisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.pipelineLTSA") + ' (' + this.translate.instant("loading.mainRecord") + ')');
        return this.http.get<PipelinesLTSADisplay>(`${environment.apiUrl}pipelineLtsa/${ltsaId}/${mainLockID}/${onlyForRead}`)
            .pipe(
                map((data: any) => {
                    let ltsaDisplay = PipelinesLTSADisplay.BuildPipelineLTSADisplay({});
                    if (data.response) {
                        ltsaDisplay = PipelinesLTSADisplay.BuildPipelineLTSADisplay(data.response);
                        if (ltsaDisplay.pipelineLtsaId) {
                            if (ltsaDisplay.hasDiffUserLockError()) {
                                this.messageHandlerService.show(ltsaDisplay.lockError, MessageType.INFO);
                            }

                            // Set the locking info into lock global list.
                            this.recordLockingFlow.setLockItem(EntityName.PIPELINE_LTSA, ltsaDisplay.pipelineLtsaId, ltsaDisplay.lockId, ltsaDisplay.lockMode);
                            this.recordLockingFlow.initLockRefreshTimer(EntityName.PIPELINE_LTSA, ltsaDisplay.pipelineLtsaId, ltsaDisplay.lockId);
                        } else {
                            this.messageHandlerService.show(ltsaDisplay.lockError, MessageType.ERROR);
                        }
                    } else {
                        const errorType = MessageHandlerService.errorType(data);
                        if (errorType === MessageType.INFO) {
                            this.entityCommonService.sendEntityNotFoundEvent();
                        } else if (errorType !== MessageType.SESSION_INVALID) {
                            this.messageHandlerService.show(MessageHandlerService.errorMessage(data), errorType);
                        }
                    }
                    return ltsaDisplay;
                }),
                catchError((error) => {
                    return of(PipelinesLTSADisplay.BuildPipelineLTSADisplay({}));
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    getLtsaById(ltsaId: number): Observable<PipelineLTSASearch> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.pipelineLTSA") + ' (' + this.translate.instant("loading.mainRecord") + ')');
        return this.http.get<PipelineLTSASearch>(`${environment.apiUrl}pipelineLtsa/${ltsaId}`)
            .pipe(
                map((data: any) => {
                        this.loadWheelService.hideWheel(wheel);
                        return this.adapter.adapt(data.response[0]);
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    saveLtsaDetails(PipelinesLTSADisplay: PipelinesLTSADisplay): Observable<PipelinesLTSADisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("saving.ltsa"));
        PipelinesLTSADisplay = this.entityUtilsService.prepareEntityForSave(PipelinesLTSADisplay) as PipelinesLTSADisplay;
        return this.http.put<PipelinesLTSADisplay>(`${environment.apiUrl}pipelineLtsa`, PipelinesLTSADisplay)
            .pipe(
                map((data: any) => {
                    if (data.response === null) {
                        if (data.exception !== null) {
                            this.messageHandlerService.showExceptionDialog(data);
                        } else if (data.message !== null) {
                            if (PipelinesLTSADisplay.saveType === SaveType.TYPE_QC || PipelinesLTSADisplay.saveType === SaveType.UPDATE) {
                                //When the user attempts to QC a record while the lock was already cleared,
                                //this//the above warning message should still be thrown; however, the record should not reload.
                                if (data.messageType !== MessageType.WARNING.valueOf()) {
                                    this.entityCommonService.sendReloadEvent();
                                }
                            } else {
                                PipelinesLTSADisplay.intDataDepMigError = data.message;
                            }

                        }
                    } else {
                        const isQcUpdateOrAmend = PipelinesLTSADisplay.saveType === SaveType.TYPE_QC ||
                            PipelinesLTSADisplay.saveType === SaveType.AMENDMENT ||
                            PipelinesLTSADisplay.saveType === SaveType.UPDATE;

                        const afterClearFunc = () => this.entityCommonService.sendPreReleaseEvent();
                        this.recordLockingFlow.clearLockFlow(EntityName.PIPELINE_LTSA, String(PipelinesLTSADisplay.pipelineLtsaId), isQcUpdateOrAmend, afterClearFunc);
                    }
                    return data.response;
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    ltsaNextId(): Observable<PipelinesLTSADisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.nextId"));
        return this.http.get<any>(`${environment.apiUrl}pipelineLtsa/nextId`)
            .pipe(
                map((data: any) => {
                    const pipelinesLTSADisplay = PipelinesLTSADisplay.BuildNewPipelineLTSA(data.response);
                    if (pipelinesLTSADisplay?.pipelineLtsaId) {
                        // Set the locking info into lock global list.
                        this.recordLockingFlow.setLockItem(EntityName.PIPELINE_LTSA, pipelinesLTSADisplay.pipelineLtsaId, -1);
                    }
                    return pipelinesLTSADisplay;
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    searchPipelineLTSAs(httpParams: HttpParams): Observable<PipelineLTSASearch[]> {
        const searchStart: number = performance.now();

        return this.http.get<PipelineLTSASearch[]>(`${environment.apiUrl}pipelineLtsa/searchPipelineLTSAs`, { params: httpParams })
            .pipe(
                map((data: any) => {
                    this.entityUtilsService.verifyMaxResult(data);
                    let ltsaList: Array<any> = data.response;

                    if (ltsaList.length > 1) {
                        ltsaList = this.removeDuplicatedLtsas(ltsaList);
                        ltsaList = this.sortLtsaById(ltsaList);
                    }
                    
                    this.telemetry.entitySearchTime("Pipeline LTSA", performance.now() - searchStart);

                    return ltsaList.map(item => this.pipelineLTSASearchAdapter.adapt(item));
                })
            )
    }

    private removeDuplicatedLtsas(ltsas: Array<any>): Array<any> {
        let uniqueLtsas: Array<any> = [];
        ltsas.forEach(ltsa => {
            if (!uniqueLtsas.some(uniqueLtsa => uniqueLtsa.PIPE_LTSA_ID == ltsa.PIPE_LTSA_ID)) {
                uniqueLtsas.push(ltsa);
            }
        });
        return uniqueLtsas;
    }

    private sortLtsaById(ltsas: Array<any>): Array<any> {
        return ltsas.sort((a, b) => {
            if (a.PIPE_LTSA_ID < b.PIPE_LTSA_ID) return -1;
            if (a.PIPE_LTSA_ID > b.PIPE_LTSA_ID) return 1;
            return 0;
        });
    }

}
